import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useAngularService } from '../../helpers/get_angular_service';
import underscored from '../../helpers/underscored';
import InitialsImage from './initials_image';
import Link from './link';
import Avatar from './avatar';
import usePermissions from 'helpers/use_permissions';

interface Params {
  [key: string]: string;
}

interface ContactLinkProps {
  id?: string | number;
  type?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  status?: string;
  archived?: boolean;
  hideBadge?: boolean;
  linkClass?: string;
  image?: string;
  withImage?: boolean;
  target?: '_self' | '_blank';
  params?: Params;
  withoutLink?: boolean;
  keepEmptyAvatarSpace?: boolean;
  imageSize?: 'lg';
  className?: string;
}

const ContactLink = ({
  id,
  type,
  status,
  name,
  firstName,
  lastName,
  archived,
  hideBadge,
  params,
  target,
  image,
  withImage,
  withoutLink,
  keepEmptyAvatarSpace,
  imageSize,
  className,
}: ContactLinkProps) => {
  const ContactBadgeService = useAngularService('ContactBadgeService');
  const permissions = usePermissions();

  const contactName = () => {
    return name || `${firstName} ${lastName}`;
  };

  const contactType = useCallback(() => {
    return underscored(type + 's');
  }, [type]);

  const badgeClass = useCallback(() => {
    return ContactBadgeService.get(type, status);
  }, [ContactBadgeService, status, type]);

  const getImage = () => {
    if (!withImage) return null;

    if (imageSize === 'lg') {
      return <Avatar image={image} name={contactName()} />;
    }

    return image ? (
      <img src={image} alt={contactName()} className="contact-avatar" />
    ) : keepEmptyAvatarSpace ? (
      <span className="contact-avatar"></span>
    ) : (
      <InitialsImage name={contactName()} />
    );
  };

  const getBadge = useCallback(() => {
    if (hideBadge) {
      return null;
    }

    const hasActualImage = withImage && (image || imageSize === 'lg');

    return type && !hasActualImage && (status || contactType() !== 'members') ? (
      <>
        <span className={classNames(`worx-badge worx-badge-sm ${badgeClass()}`)}></span>{' '}
      </>
    ) : null;
  }, [badgeClass, contactType, hideBadge, status, type, withImage, image, imageSize]);

  return (
    <span>
      {archived || !type || !id || withoutLink || !permissions.view_contact_profile_page ? (
        <span className={classNames('contact-link', className)}>
          {getBadge()}

          {getImage()}
          {contactName()}
        </span>
      ) : (
        <Link
          route="gym.contacts.edit"
          routeParams={{ contactId: id, contactType: contactType(), ...params }}
          target={target}
          className={classNames('contact-link', className)}
        >
          {getBadge()}

          {getImage()}
          {contactName()}
        </Link>
      )}
    </span>
  );
};

export default ContactLink;
